import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Save, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { TemplateStageEditor, newKey, move } from '@/components/tasks/TemplateStageEditor';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function TaskTemplates() {
  const [templates, setTemplates] = useState([]);
  const [active, setActive] = useState('Supply Only');
  const [stages, setStages] = useState([]);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/task-templates`, { withCredentials: true })
      .then(r => setTemplates(r.data)).catch(() => toast.error('Failed to load templates'));
  }, []);

  useEffect(() => {
    const t = templates.find(x => x.project_type === active);
    if (t) { setStages(t.stages); setDirty(false); }
  }, [templates, active]);

  const update = (s) => { setStages(s); setDirty(true); };
  const allOptions = stages.flatMap(s => s.tasks.filter(t => t.title.trim()).map(t => ({ id: t.key, label: t.title, group: s.name || 'Untitled stage' })));

  const switchTab = (v) => {
    if (dirty && !window.confirm('You have unsaved changes. Discard them?')) return;
    setActive(v);
  };

  const persist = async (call, msg) => {
    try {
      const r = await call();
      setTemplates(ts => ts.map(t => t.project_type === active ? r.data : t));
      toast.success(msg);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to save'); }
  };

  const save = () => {
    const keys = new Set(stages.flatMap(s => s.tasks.map(t => t.key)));
    const clean = stages.map(s => ({ ...s, tasks: s.tasks.map(t => ({ ...t, depends_on: (t.depends_on || []).filter(d => keys.has(d)) })) }));
    persist(() => axios.put(`${BACKEND_URL}/api/task-templates/${encodeURIComponent(active)}`, { stages: clean }, { withCredentials: true }), 'Template saved — new projects will use it');
  };

  const reset = () => {
    if (!window.confirm(`Reset the ${active} template to the original default checklist?`)) return;
    persist(() => axios.post(`${BACKEND_URL}/api/task-templates/${encodeURIComponent(active)}/reset`, {}, { withCredentials: true }), 'Template reset');
  };

  return (
    <div className="space-y-6 pt-10 lg:pt-0" data-testid="task-templates-page">
      <div className="flex flex-wrap justify-between items-end gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-stone-900 tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Task Templates</h1>
          <p className="mt-2 text-base text-stone-600">The checklist every new project starts with. Changes apply to projects created after you save.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={reset} data-testid="tmpl-reset-btn"><RotateCcw className="w-4 h-4 mr-2" />Reset to default</Button>
          <Button onClick={save} disabled={!dirty} data-testid="tmpl-save-btn" className="bg-amber-700 hover:bg-amber-800 text-white"><Save className="w-4 h-4 mr-2" />Save template</Button>
        </div>
      </div>

      <Tabs value={active} onValueChange={switchTab}>
        <TabsList className="bg-stone-100">
          {['Supply Only', 'Turnkey Contract', 'Hybrid'].map(t => (
            <TabsTrigger key={t} value={t} data-testid={`tmpl-tab-${t.replace(/\s+/g, '-').toLowerCase()}`}>{t}</TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="space-y-4">
        {stages.map((s, si) => (
          <TemplateStageEditor key={si} stage={s} si={si} total={stages.length} allOptions={allOptions}
            onChange={(ns) => update(stages.map((x, i) => i === si ? ns : x))}
            onMove={(d) => update(move(stages, si, d))}
            onDelete={() => window.confirm(`Delete stage "${s.name}" and its ${s.tasks.length} tasks?`) && update(stages.filter((_, i) => i !== si))} />
        ))}
        <button onClick={() => update([...stages, { name: 'New stage', tasks: [{ key: newKey(), title: '', description: '', depends_on: [] }] }])}
          data-testid="tmpl-add-stage-btn" className="w-full border border-dashed border-stone-300 rounded-lg py-4 text-sm text-stone-600 hover:border-amber-700 hover:text-amber-800 transition-colors inline-flex items-center justify-center gap-2">
          <Plus className="w-4 h-4" /> Add stage
        </button>
      </div>
    </div>
  );
}
